import ContentShell from "components/ContentShell";
import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import useStyles from "styles/banner/bannerEditor";
import { SimpleTextInput } from "components/FormTextInput";
import {
  createDailyReward,
  updateDailyReward,
  getDailyRewardsInfo,
} from "services/dailyRewardService";
import { SimpleSelectInput } from "components/FormSelectInput";
import { getUniquesArrayObject } from "utils/miscUtils";
import debounce from "lodash.debounce";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import SearchableInput from "components/SearchableInput";
import { getBoosters, getPlayerPacks, packInfo } from "services/packServices";
import { dailyRewardsValidation } from "utils/validation";

const INITIAL_DAILY_REWARDS = {
  pack: "",
  booster: "",
  day: "",
  awardType: "PACK",
  rewardHC: 0,
};

const AWARD_TYPES = [
  { id: "PACK", label: "PACK" },
  { id: "COINS", label: "COINS" },
  { id: "BOOSTER", label: "BOOSTER" },
];

const DailyRewardsEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const id = location.pathname.split("/").slice(-1)[0];
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [dailyRewards, setDailyRewards] = useState(INITIAL_DAILY_REWARDS);
  const navigate = useNavigate();

  const [options, setOptions] = useState({
    playerPacks: [],
    playerPackOptions: [],
    boostersOptions: [],
  });

  const cancel = useCallback(
    () => navigate(configData.routes.dailyRewards.dailyRewards),
    [navigate]
  );

  const fetchItemInfo = async (itemId, type) => {
    try {
      if (type === "pack") {
        const packInfoRes = await packInfo(itemId);
        return {
          id: packInfoRes?.data?._id,
          label: `${packInfoRes?.data?.name} - ${packInfoRes?.data?.type}`,
        };
      } else if (type === "booster") {
        const boosters = await getBoosters();
        const booster = boosters.data.find((b) => b._id === itemId);
        return {
          id: booster?._id,
          label: booster?.displayName,
        };
      }
    } catch (error) {
      console.error(`Failed to fetch ${type} info:`, error);
      return null;
    }
  };

 const transformData = (data, type) => {
   return (
     data?.map((item) => ({
       id: item?._id || item?.id, // Handle both cases
       label:
         type === "pack" ? `${item.name} - ${item.type}` : item?.displayName,
     })) || []
   );
 };

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      const [boostersResp, playerPacksResp] = await Promise.all([
        getBoosters(),
        getPlayerPacks({ pageSize: defaultPageSize, pageNo: defaultPageNo }),
      ]);

      const boostersOptions = transformData(boostersResp.data, "booster");
      const playerPackOptions = transformData(playerPacksResp?.items, "pack");

      setOptions({
        playerPacks: playerPacksResp?.items || [],
        playerPackOptions,
        boostersOptions,
      });

      if (id !== "create") {
        const dailyRewardsResp = await getDailyRewardsInfo(id);
        let updatedRewards = { ...dailyRewardsResp.data };

        if (dailyRewardsResp.data.awardType === "PACK") {
          let selectedPack = playerPackOptions.find(
            (option) => option.id === dailyRewardsResp.data.pack
          );

          if (!selectedPack) {
            selectedPack = await fetchItemInfo(
              dailyRewardsResp.data.pack,
              "pack"
            );
          }
          updatedRewards.pack = selectedPack || dailyRewardsResp.data.pack;
        } else if (dailyRewardsResp.data.awardType === "BOOSTER") {
          let selectedBooster = boostersOptions.find(
            (option) => option.id === dailyRewardsResp.data.booster
          );

          if (!selectedBooster) {
            selectedBooster = await fetchItemInfo(
              dailyRewardsResp.data.booster,
              "booster"
            );
          }
          updatedRewards.booster =
            selectedBooster || dailyRewardsResp.data.booster;
        }

        setDailyRewards(updatedRewards);
      }
    } catch (error) {
      showSnackbar(error.message || "Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  }, [id, showSnackbar]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce(async (searchValue, type) => {
      if (!searchValue) {
        const defaultOptions =
          type === "pack"
            ? transformData(options.playerPacks, "pack")
            : transformData(options.boostersOptions, "booster");

        setOptions((prev) => ({
          ...prev,
          [type === "pack" ? "playerPackOptions" : "boostersOptions"]:
            defaultOptions,
        }));
        return;
      }

      try {
        const response =
          type === "pack"
            ? await getPlayerPacks({
                search: searchValue,
                skipPagination: true,
              })
            : await getBoosters();

        const newOptions = transformData(
          response?.[type === "pack" ? "items" : "data"],
          type
        );

        setOptions((prev) => ({
          ...prev,
          [type === "pack" ? "playerPackOptions" : "boostersOptions"]:
            getUniquesArrayObject([
              ...newOptions,
              ...(prev[
                type === "pack" ? "playerPackOptions" : "boostersOptions"
              ] || []),
            ]),
        }));
      } catch (error) {
        console.error("Search Error:", error);
        showSnackbar(error.message, "error");
      }
    }, 300),
    [options.playerPacks, options.boostersOptions, showSnackbar]
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);

      const payload = {
        awardType: dailyRewards.awardType,
        day: parseInt((dailyRewards as any).day),
        // Send null for pack if not PACK type or not provided
        pack:
          dailyRewards.awardType === "PACK"
            ? (dailyRewards.pack as any)?.id || null
            : null,
        // Send null for booster if not BOOSTER type or not provided
        booster:
          dailyRewards.awardType === "BOOSTER"
            ? (dailyRewards.booster as any)?.id || null
            : null,
        // Send 0 for rewardHC if not COINS type (or keep the value if COINS type)
        rewardHC:
          dailyRewards.awardType === "COINS"
            ? parseInt(String(dailyRewards.rewardHC))
            : null,
        // Include ID if editing
        ...(id !== "create" && { _id: (dailyRewards as any)._id }),
      };

      await dailyRewardsValidation.validate(payload);
      const response =
        id === "create"
          ? await createDailyReward(payload)
          : await updateDailyReward(payload);

      showSnackbar(response.message, "success");
      cancel();
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setDailyRewards((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "awardType" && {
        pack: null,
        booster: null,
        rewardHC: null,
      }),
    }));
  };

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={cancel}
      title={`${id === "create" ? "Create" : "Edit"} Daily Reward`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.container}>
        <Box className={styles.firstFormContainer}>
          <Box className={styles.field}>
            <SimpleSelectInput
              label="Award Type"
              name="awardType"
              value={dailyRewards.awardType}
              options={AWARD_TYPES}
              onChange={handleChange}
            />
          </Box>
          <Box className={styles.field}>
            <SimpleTextInput
              label="Day"
              name="day"
              value={dailyRewards.day}
              onChange={handleChange}
            />
          </Box>
          {dailyRewards.awardType === "PACK" && (
            <Box className={styles.field}>
              <SearchableInput
                selected={dailyRewards.pack}
                label="Pack"
                name="pack"
                options={options.playerPackOptions}
                onInputChange={(value) => handleSearch(value, "pack")}
                onOptionSelect={handleChange}
                placeholder="Select Pack"
              />
            </Box>
          )}
          {dailyRewards.awardType === "BOOSTER" && (
            <Box className={styles.field}>
              <SearchableInput
                selected={dailyRewards.booster}
                label="Booster"
                name="booster"
                options={options.boostersOptions}
                onInputChange={(value) => handleSearch(value, "booster")}
                onOptionSelect={handleChange}
                placeholder="Select Booster"
              />
            </Box>
          )}
          {dailyRewards.awardType === "COINS" && (
            <Box className={styles.field}>
              <SimpleTextInput
                label="Reward HC"
                name="rewardHC"
                value={dailyRewards.rewardHC}
                onChange={handleChange}
              />
            </Box>
          )}
        </Box>
      </Box>
    </ContentShell>
  );
};

export default DailyRewardsEditor;
